import { useQueryClient } from "@tanstack/react-query";
import { ExportType, Frequency } from "PFApp/analytics/types/export_analytics";
import { useGrowl } from "PFApp/use_growl";
import { ActionIcon } from "PFComponents/action_icon";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import Modal from "PFComponents/modal/modal";
import { Typography } from "PFComponents/typography";
import { useGenerateReport } from "PFCore/hooks/queries/analytics/booking_export/use_generate_report";
import { queryKeys } from "PFCore/hooks/queries/analytics/query_keys";
import { useGetTemplate } from "PFCore/hooks/queries/analytics/reports/use_get_template";
import React, { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAnalyticsModalContext } from "../analytics_modal_context_provider";
import { ExportWizardContent } from "../export_wizard_content";
import { decamelizeFilters } from "../export_wizard_content/export_fields_utils";
import { convertFieldsResponse, getTemplateReportType } from "../export_wizard_content/helpers";
import { useGeneratePayloadProvider } from "../export_wizard_content/tabs/export_tab/use_generate_payload_provider";
import css from "./export_wizard_modal.module.scss";

export const ExportWizardModal = (): JSX.Element => {
  const {
    close,
    state: { data },
    updateData
  } = useAnalyticsModalContext();
  const queryClient = useQueryClient();
  const { currentTab } = data || {};
  const { t } = useTranslation(["insights", "translation"]);
  const { data: template, isFetching } = useGetTemplate();
  const growl = useGrowl();
  const provideGeneratePayload = useGeneratePayloadProvider();

  useEffect(() => {
    if (template) {
      updateData({
        frequency: (template.aggregateBy || Frequency.Daily) as Frequency,
        selectedFilters: decamelizeFilters(template.filters),
        selectedFields: convertFieldsResponse(template.fields),
        reportType: getTemplateReportType(template),
        currentTab: "fields",
        ...(template.name
          ? { selectedTemplate: template, exportType: ExportType.Template }
          : { exportType: ExportType.Custom })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const { mutateAsync: generateReport, isLoading } = useGenerateReport({
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.templates);
      growl({
        kind: "success",
        message: (
          <Typography variant="bodyBold" noMargin>
            {`${data.reportName} ${t("insights:exportWizard.orderSuccess")}`}
          </Typography>
        )
      });
    },
    onError: () => {
      growl({
        kind: "error",
        message: t("insights:exportWizard.generateFailed")
      });
    }
  });

  const handleOK = async () => {
    if (data.reportName) {
      const payload = provideGeneratePayload(data.reportName);
      await generateReport(payload);
      close();
    }
  };

  return (
    //@ts-ignore-line
    <Modal
      closeOnBackdropClick
      handleClose={close}
      classes={{
        dialog: css.dialog
      }}
    >
      <SectionBox className={css.wrapper}>
        {isFetching ? (
          <LoadingDots circlesEnabled pageViewEnabled />
        ) : (
          <>
            <div className={css.header}>{t("insights:exportWizard.exportReport")}</div>
            <ActionIcon name="cross" size="xs" onClick={close} classes={{ root: css.close }} />

            <ExportWizardContent />

            <div className={css.footer}>
              <Button text={t("insights:exportWizard.cancel")} kind="secondary" onClick={close} />
              {currentTab === "export" && (
                <div className={css.generateSection}>
                  <Typography variant="bodyRegular" noMargin>
                    {t("insights:exportWizard.generateDescription")}
                  </Typography>
                  <Button
                    text={t("insights:exportWizard.generate")}
                    onClick={handleOK}
                    disabled={!data.reportName || isLoading}
                    kind="landing-blue"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </SectionBox>
    </Modal>
  );
};
